import { Box, TextField, InputAdornment, Typography, Autocomplete, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FlightIcon from '@mui/icons-material/Flight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LuggageIcon from '@mui/icons-material/Luggage';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import SpeedIcon from '@mui/icons-material/Speed';
import TollIcon from '@mui/icons-material/Toll';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import { useEffect, useMemo } from 'react';
import { useGetApiOrganizationsQuery } from '../../app/athenaApiGen';

/**
 * ExpenseRows: Dynamic expense rows component for TAR form
 * - Rows are fully defined by schema configuration
 * - Each row can have different visible fields
 * - Supports auto-calculating totals that can also be manually edited
 *
 * Props:
 * - field: {
 *     name,
 *     label,
 *     rows: Array<{ type, label, icon, fields: Array<fieldName>, fixed, editableTotal }>,
 *     fieldLabels: { fieldName: label }
 *   }
 *
 * Row config options:
 * - editableTotal: when true, totalEstimatedCost is directly editable (for lump-sum entries)
 * - formData: parent object containing the expenses array
 * - setFormData: function to update parent
 * - readOnly: boolean
 */
export default function ExpenseRows(props) {
  const fieldName = props.field.name;
  const expenses = useMemo(() => props.formData?.[fieldName] || [], [props.formData, fieldName]);

  // Consistent row height for all fields
  const ROW_HEIGHT = '56px';

  // Get row configurations from schema
  const rowConfigs = useMemo(() => props.field.rows || [], [props.field.rows]);
  const fieldLabels = props.field.fieldLabels || {};

  // Fetch organizations from API
  const { data: rentalCarData } = useGetApiOrganizationsQuery({ organizationType: 'Rental Car Vendor', pageSize: 100 });
  const { data: hotelData } = useGetApiOrganizationsQuery({ organizationType: 'Hotel Vendor', pageSize: 100 });
  const { data: airlineData } = useGetApiOrganizationsQuery({ organizationType: 'Airline Vendor', pageSize: 100 });

  // Extract organization lists
  const rentalCarVendors = useMemo(() => rentalCarData?.items || [], [rentalCarData]);
  const hotelVendors = useMemo(() => hotelData?.items || [], [hotelData]);
  const airlineVendors = useMemo(() => airlineData?.items || [], [airlineData]);

  // Initialize with configured rows if not already present. 'other'
  // rows are *not* seeded — the user adds them on demand via the
  // "+ Add Other Expense" button below the table.
  useEffect(() => {
    if (expenses.length === 0 && rowConfigs.length > 0) {
      const initialExpenses = rowConfigs
        .filter((config) => config.type !== 'other')
        .map((config) => {
          const row = { type: config.type, fixed: config.fixed !== false };
          ['expense', 'organizationName', 'perDiem', 'numberOfDays', 'itemUnitCost', 'totalEstimatedCost'].forEach((field) => {
            row[field] = '';
          });
          return row;
        });
      props.setFormData((prev) => ({ ...prev, [fieldName]: initialExpenses }));
    }
  }, [expenses.length, fieldName, props, rowConfigs]);

  // (Removed: auto-add new empty Other row when the last has data.
  // We intentionally keep exactly one Other Expenses row so the user
  // can Tab straight out to the next field without first traversing a
  // freshly-created empty row.)

  const updateExpense = (index, field, value, organizationName = null) => {
    const updated = [...expenses];
    updated[index] = { ...updated[index], [field]: value };

    // Save organizationName if provided (for vendor autocomplete fields)
    // 'expense' field stores the organizationId, 'organizationName' stores the display name
    if (organizationName !== null) {
      updated[index].organizationName = organizationName;
    }

    // Auto-calculate totalEstimatedCost: itemUnitCost * numberOfDays (or quantity).
    // Skipped for mealsIncidentals — that row's total is driven by the GSA
    // per-diem formula (first & last day at 75%) owned by IMCSTARForm, so
    // days × rate would clobber the formula-computed total.
    if (
      (field === 'numberOfDays' || field === 'itemUnitCost') &&
      updated[index].type !== 'mealsIncidentals'
    ) {
      const daysRaw = updated[index].numberOfDays;
      const costRaw = updated[index].itemUnitCost;
      const daysFilled = daysRaw !== '' && daysRaw != null;
      const costFilled = costRaw !== '' && costRaw != null;
      // Only blank the total when BOTH cells are empty. If the user
      // explicitly entered 0 in either cell, render the resulting 0.00
      // so the row reads as "calculated", not "missing".
      if (daysFilled || costFilled) {
        const days = parseFloat(daysRaw) || 0;
        const cost = parseFloat(costRaw) || 0;
        updated[index].totalEstimatedCost = (days * cost).toFixed(2);
      } else {
        updated[index].totalEstimatedCost = '';
      }
    }
    // If manually editing total, just update it (no auto-calc)

    props.setFormData((prev) => ({ ...prev, [fieldName]: updated }));
  };

  // No-op blur handler. We intentionally keep the one Other Expenses
  // row exactly as the user left it — no auto-cleanup or auto-add.
  const handleBlur = () => {
  };

  const getIcon = (type) => {
    const config = rowConfigs.find(r => r.type === type);
    if (!config || !config.icon) return null;

    const iconMap = {
      'perDiem': AttachMoneyIcon,
      'hotel': HotelIcon,
      'car': DirectionsCarIcon,
      'airfare': FlightIcon,
      'other': MoreHorizIcon,
      'baggage': LuggageIcon,
      'meals': RestaurantIcon,
      'fuel': LocalGasStationIcon,
      'parking': LocalParkingIcon,
      'pov': SpeedIcon,
      'tolls': TollIcon,
      'transit': DirectionsTransitIcon,
      'rideShare': LocalTaxiIcon,
    };

    const IconComponent = iconMap[config.icon] || iconMap[type];
    return IconComponent ? <IconComponent sx={{ fontSize: 18, color: 'text.secondary' }} /> : null;
  };

  const getRowConfig = (type) => {
    return rowConfigs.find(r => r.type === type) || {};
  };

  const isFieldVisible = (rowConfig, fieldName) => {
    // If fields array is defined, check if this field is included
    if (rowConfig.fields && Array.isArray(rowConfig.fields)) {
      return rowConfig.fields.includes(fieldName);
    }
    // Default: show all fields
    return true;
  };

  const getVendorOptions = (type) => {
    switch (type) {
      case 'airfare':
        return airlineVendors;
      case 'hotel':
        return hotelVendors;
      case 'car':
        return rentalCarVendors;
      default:
        return [];
    }
  };

  const shouldUseAutocomplete = (type) => {
    return ['airfare', 'hotel', 'car'].includes(type);
  };

  const getDisplayValue = (expense) => {
    // If organizationName is set, use it for display
    if (expense.organizationName) {
      return expense.organizationName;
    }
    // Otherwise, try to find the name by looking up the organizationId
    if (expense.expense && shouldUseAutocomplete(expense.type)) {
      const options = getVendorOptions(expense.type);
      const match = options.find(opt => opt.organizationId === expense.expense);
      if (match) return match.name;
    }
    // Fallback to empty string
    return '';
  };

  // ────────────────────────────────────────────────────────────────────
  // Cell renderers — extracted so the two-section layout below can reuse
  // them without duplicating ~100 lines each. Each takes the row's
  // original index in `expenses` (preserved through filtering) so state
  // updates address the correct entry.
  // ────────────────────────────────────────────────────────────────────

  const renderItemCell = (expense, idx, rowConfig, flexValue) => {
    // perDiem and label-only rows render a plain icon + label (no input).
    const labelOnly =
      expense.type === 'perDiem' ||
      (!isFieldVisible(rowConfig, 'expense') && rowConfig?.label);

    if (labelOnly) {
      return (
        <Box
          flex={flexValue}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: ROW_HEIGHT,
            gap: 1,
            px: 1.5,
          }}
        >
          {getIcon(expense.type) && (
            <Box sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>
              {getIcon(expense.type)}
            </Box>
          )}
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {rowConfig.label || (expense.type === 'perDiem' ? 'Per Diem' : '')}
          </Typography>
        </Box>
      );
    }

    if (!isFieldVisible(rowConfig, 'expense')) return null;

    // Editable expense row: one full-height boxed input. Icon sits as a
    // startAdornment, the row label (Airfare / Lodging / Rental Car / Other)
    // is the floating field label. Height matches the Qty / Cost inputs so
    // the row sits flush.
    const iconNode = rowConfig.hideIcon ? null : getIcon(expense.type);
    // Per-type placeholder copy so the box gives the user a clear nudge
    // when empty (and the floating label is still tucked into the border).
    const expensePlaceholders = {
      airfare: 'Select airline',
      hotel: 'Select lodging',
      car: 'Select rental car agency',
      other: 'Describe other expenses',
    };
    const placeholder = expensePlaceholders[expense.type] || '';
    return (
      <Box flex={flexValue} sx={{ minWidth: 0 }}>
        {shouldUseAutocomplete(expense.type) ? (
          <Autocomplete
            freeSolo
            options={getVendorOptions(expense.type)}
            getOptionLabel={(option) => typeof option === 'string' ? option : (option.name || '')}
            value={getDisplayValue(expense)}
            onChange={(event, newValue) => {
              if (typeof newValue === 'object' && newValue !== null) {
                updateExpense(idx, 'expense', newValue.organizationId, newValue.name);
              } else {
                updateExpense(idx, 'expense', newValue || '', newValue || '');
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                updateExpense(idx, 'expense', newInputValue || '', newInputValue || '');
              }
            }}
            readOnly={props.readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                id={`field-${fieldName}-${idx}-expense`}
                variant="outlined"
                fullWidth
                label={rowConfig.hideLabel ? undefined : (rowConfig.label || 'Expense')}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  readOnly: props.readOnly,
                  startAdornment: (
                    <>
                      {iconNode && (
                        <InputAdornment position="start">{iconNode}</InputAdornment>
                      )}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  sx: {
                    ...params.InputProps?.sx,
                    height: ROW_HEIGHT,
                  },
                }}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    id: `field-${fieldName}-${idx}-expense`,
                    name: `${fieldName}.${idx}.expense`,
                  },
                }}
              />
            )}
          />
        ) : (
          <TextField
            id={`field-${fieldName}-${idx}-expense`}
            variant="outlined"
            fullWidth
            label={rowConfig.hideLabel ? undefined : (rowConfig.label || 'Expense')}
            placeholder={placeholder}
            value={expense.expense || ''}
            onChange={(e) => updateExpense(idx, 'expense', e.target.value)}
            InputProps={{
              readOnly: props.readOnly,
              startAdornment: iconNode ? (
                <InputAdornment position="start">{iconNode}</InputAdornment>
              ) : undefined,
              sx: { height: ROW_HEIGHT },
            }}
            slotProps={{
              htmlInput: {
                id: `field-${fieldName}-${idx}-expense`,
                name: `${fieldName}.${idx}.expense`,
              },
            }}
          />
        )}
      </Box>
    );
  };

  // Tab skips any cell that already has a value (auto-filled or
  // previously typed) so the user lands on the next *vacant* cell.
  // Users can still click into a filled cell to edit it.
  const renderQtyCell = (expense, idx, rowConfig) => (
    <Box flex="1 1 150px">
      <TextField
        id={`field-${fieldName}-${idx}-numberOfDays`}
        variant="outlined"
        aria-label={rowConfig.quantityLabel || fieldLabels.numberOfDays || 'Number of Days'}
        placeholder={rowConfig.quantityLabel || fieldLabels.numberOfDays || 'Number of Days'}
        value={expense.numberOfDays || ''}
        onChange={(e) => updateExpense(idx, 'numberOfDays', e.target.value)}
        fullWidth
        InputProps={{
          readOnly: props.readOnly,
          sx: { height: ROW_HEIGHT },
        }}
        slotProps={{
          htmlInput: {
            id: `field-${fieldName}-${idx}-numberOfDays`,
            name: `${fieldName}.${idx}.numberOfDays`,
            tabIndex: expense.numberOfDays ? -1 : undefined,
          },
        }}
      />
    </Box>
  );

  const renderRateCell = (expense, idx, rowConfig) => (
    <Box flex="1 1 150px">
      <TextField
        id={`field-${fieldName}-${idx}-itemUnitCost`}
        variant="outlined"
        aria-label={(fieldLabels.itemUnitCost || 'Item/Unit Cost').replace(/^\$\s*/, '')}
        value={expense.itemUnitCost || ''}
        onChange={(e) => updateExpense(idx, 'itemUnitCost', e.target.value)}
        fullWidth
        // mutedRate: render the cell with the bold + muted look used by
        // hardcoded fields, but keep it editable. Used for the POV
        // mileage rate, which is a federal default that can change.
        sx={rowConfig?.mutedRate
          ? { opacity: 0.6, '& .MuiInputBase-input': { fontWeight: 700 } }
          : undefined}
        InputProps={{
          readOnly: props.readOnly,
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          sx: { height: ROW_HEIGHT },
        }}
        slotProps={{
          htmlInput: {
            id: `field-${fieldName}-${idx}-itemUnitCost`,
            name: `${fieldName}.${idx}.itemUnitCost`,
            tabIndex: expense.itemUnitCost ? -1 : undefined,
          },
        }}
      />
    </Box>
  );

  // Estimated Cost cells accept decimal values only — digits and an
  // optional single decimal point. Strip everything else as the user
  // types so alpha / special characters never reach formData.
  const sanitizeDecimal = (raw) => {
    if (raw == null) return '';
    const digitsAndDots = String(raw).replace(/[^0-9.]/g, '');
    const parts = digitsAndDots.split('.');
    if (parts.length <= 1) return digitsAndDots;
    return `${parts[0]}.${parts.slice(1).join('')}`;
  };

  const renderCostCell = (expense, idx, rowConfig) => {
    const isReadOnlyTotal = expense.type !== 'airfare' && !rowConfig?.editableTotal;
    return (
      <Box flex="1 1 150px">
        <TextField
          id={`field-${fieldName}-${idx}-totalEstimatedCost`}
          variant="outlined"
          aria-label={(fieldLabels.totalEstimatedCost || 'Total Estimated Cost').replace(/^\$\s*/, '')}
          value={expense.totalEstimatedCost || ''}
          onChange={(e) => updateExpense(idx, 'totalEstimatedCost', sanitizeDecimal(e.target.value))}
          fullWidth
          inputMode="decimal"
          InputProps={{
            readOnly: props.readOnly || isReadOnlyTotal,
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            sx: { height: ROW_HEIGHT },
          }}
          slotProps={{
            htmlInput: {
              id: `field-${fieldName}-${idx}-totalEstimatedCost`,
              name: `${fieldName}.${idx}.totalEstimatedCost`,
              // Skip read-only totals (days × rate auto-computed) and
              // any total that already has a value — Tab lands only on
              // empty editable totals (Airfare, Baggage, etc.).
              tabIndex: (isReadOnlyTotal || expense.totalEstimatedCost) ? -1 : undefined,
            },
          }}
        />
      </Box>
    );
  };

  // Categorize expenses: rows with a quantity (numberOfDays) belong in the
  // Per-Unit section (qty × rate = cost). Everything else is a flat
  // subtotal-only row. `other` rows are pulled out and rendered last so the
  // catchall "Describe other expenses" sits at the bottom of all expenses.
  const taggedRows = expenses.map((expense, idx) => ({
    expense,
    idx,
    rowConfig: getRowConfig(expense.type),
  }));
  const otherRows = taggedRows.filter(({ expense }) => expense.type === 'other');
  // Regular rows (non-other) render in schema order — flat-rate rows and
  // per-unit (qty × rate) rows share a single 4-column grid so the visual
  // order matches the order declared in the form schema. Rows without a
  // Quantity / Rate render those cells as blank spacers.
  const regularRows = taggedRows.filter(({ expense }) => expense.type !== 'other');

  // Reusable styling fragments.
  const sectionSubheaderSx = {
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: 'text.secondary',
    mt: 2,
    mb: 1,
  };
  const columnHeaderCellSx = {
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: 'text.secondary',
  };
  const columnHeaderRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mb: 0.75,
    px: 0.5,
    py: 0.75,
    borderBottom: '1px solid',
    borderColor: 'divider',
  };
  const dataRowSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.75,
    pb: 0.5,
    mb: 0.5,
    position: 'relative',
    // Subtle row divider — keeps each expense line associated with its
    // cost column across a wide row without adding visual noise.
    borderBottom: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {props.field.header !== false && props.field.label && (
        <Typography variant="subtitle1" sx={{ fontFamily: 'Arial, sans-serif', mb: 1.5, mt: 1.5 }}>
          {props.field.label}
        </Typography>
      )}

      {/* ── Regular expense rows (schema order). Item:2 + Qty:1 + Rate:1
          + Cost:1 grid. Rows without a Quantity field render blank Qty
          and Rate cells so the columns line up across all rows. */}
      {regularRows.length > 0 && (
        <>
          <Box sx={columnHeaderRowSx}>
            <Box flex="2 1 300px" />
            <Box flex="1 1 150px">
              <Typography variant="caption" sx={columnHeaderCellSx}>
                Quantity
              </Typography>
            </Box>
            <Box flex="1 1 150px">
              <Typography variant="caption" sx={columnHeaderCellSx}>
                Rate / Unit Cost
              </Typography>
            </Box>
            <Box flex="1 1 150px">
              <Typography variant="caption" sx={columnHeaderCellSx}>
                Estimated Cost
              </Typography>
            </Box>
          </Box>
          {regularRows.map(({ expense, idx, rowConfig }) => {
            const hasQty = isFieldVisible(rowConfig, 'numberOfDays');
            return (
              <Box key={idx} sx={dataRowSx} onBlur={handleBlur}>
                {renderItemCell(expense, idx, rowConfig, '2 1 300px')}
                {hasQty ? renderQtyCell(expense, idx, rowConfig) : <Box flex="1 1 150px" />}
                {hasQty ? renderRateCell(expense, idx, rowConfig) : <Box flex="1 1 150px" />}
                {renderCostCell(expense, idx, rowConfig)}
              </Box>
            );
          })}
        </>
      )}

      {/* ── Section 3: Other (catchall, rendered last). Three cells now:
          a label cell aligned with the Item column above (2 1 300px), a
          narrower description input spanning Qty + Rate widths combined
          (2 1 300px), and the standard cost column (1 1 150px). The
          input renders without an icon or floating label since the left
          label cell already shows "Other Expenses". */}
      {otherRows.length > 0 &&
        otherRows.map(({ expense, idx, rowConfig }) => (
          <Box key={idx} sx={dataRowSx} onBlur={handleBlur}>
            <Box
              flex="2 1 300px"
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: ROW_HEIGHT,
                gap: 1,
                px: 1.5,
              }}
            >
              {getIcon(expense.type) && (
                <Box sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>
                  {getIcon(expense.type)}
                </Box>
              )}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {rowConfig.label || ''}
              </Typography>
            </Box>
            {renderItemCell(
              expense,
              idx,
              { ...rowConfig, hideLabel: true, hideIcon: true },
              '2 1 300px',
            )}
            {renderCostCell(expense, idx, rowConfig)}
          </Box>
        ))}

      {/* + Add Other Expense — manually append a new empty Other row. */}
      {!props.readOnly && rowConfigs.some((r) => r.type === 'other') && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1, mb: 0.5 }}>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => {
              const newRow = { type: 'other', fixed: false };
              ['expense', 'perDiem', 'numberOfDays', 'itemUnitCost', 'totalEstimatedCost', 'organizationName'].forEach(
                (field) => { newRow[field] = ''; },
              );
              props.setFormData((prev) => ({
                ...prev,
                [fieldName]: [...(prev?.[fieldName] || []), newRow],
              }));
            }}
            sx={{
              textTransform: 'none',
              fontSize: 'body1.fontSize',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Add Other Expense
          </Button>
        </Box>
      )}

      {/* ── Footer: Total Estimated Trip Cost ──────────────────────────
          Sits inside the same section card so the running total reads as
          the last row of the table. The left spacer keeps the value box
          aligned with the Estimated Cost column above (same flex ratio). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          flex="4 1 600px"
          sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1.5 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Total Estimated Trip Cost
          </Typography>
        </Box>
        <Box flex="1 1 150px">
          <TextField
            id="field-totalNTETripCost"
            variant="outlined"
            fullWidth
            value={
              props.formData?.totalNTETripCost != null
                ? Number(props.formData.totalNTETripCost).toFixed(2)
                : ''
            }
            placeholder="0.00"
            InputProps={{
              readOnly: true,
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { height: ROW_HEIGHT, fontWeight: 700 },
            }}
            slotProps={{
              htmlInput: {
                id: 'field-totalNTETripCost',
                name: 'totalNTETripCost',
                'aria-label': 'Total Estimated Trip Cost',
                // Read-only running total — skip in tab order so Tab
                // jumps straight from the last expense row to the next
                // editable field (Approval Due Date in the heading).
                tabIndex: -1,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
